import apiClient from './axios';
import { ApiResponse } from '../types';
import { MaybeApproval } from './approvals';

/**
 * Savings accounts against what their goals say they hold.
 *
 * Each person's savings live in a Safe Haven account in their own name, and
 * every goal balance is a share of it. These endpoints show where the two
 * disagree, and the moves between someone's main account and their savings
 * that the bank never answered for.
 *
 * Read-only apart from resolving a stuck move, which credits or refunds a
 * goal and therefore goes through maker-checker.
 */

export interface SavingsPerson {
  name: string | null;
  email: string | null;
  phone: string | null;
}

export interface SavingsAccountRow {
  userId: string;
  person: SavingsPerson;
  /** Masked: last four digits only. */
  account: string;
  accountName: string | null;
  openedAt: string;
  goalsTotal: number;
  inFlight: number;
}

export interface SavingsAccountPage {
  items: SavingsAccountRow[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface SavingsReconciliation {
  account: { account: string; name: string | null; openedAt: string } | null;
  /** Null when the bank did not answer — not zero. */
  bankBalance: number | null;
  goalsTotal: number;
  /** Bank less goals. Null when the bank did not answer. */
  difference: number | null;
  goals: Array<{
    id: string;
    name: string;
    type: string;
    status: string;
    savedAmount: number;
  }>;
  inFlight: Array<{
    id: string;
    direction: 'in' | 'out';
    amount: number;
    createdAt: string;
  }>;
}

export interface SavingsCheckResult {
  checked: number;
  capped: boolean;
  mismatched: number;
  unreachable: number;
  findings: Array<
    SavingsReconciliation & { userId: string; person: SavingsPerson | null }
  >;
}

export interface StuckSavingsMove {
  id: string;
  userId: string;
  person: SavingsPerson | null;
  goalId: string;
  goalName: string | null;
  /** `in`: main account → savings. `out`: savings → main account. */
  direction: 'in' | 'out';
  amount: number;
  reference: string;
  createdAt: string;
}

export const savingsReconciliationApi = {
  accounts: async (params: { q?: string; page?: number; limit?: number }) => {
    const { data } = await apiClient.get<ApiResponse<SavingsAccountPage>>(
      '/admin/savings/accounts',
      { params },
    );
    return data.data;
  },

  /** One person's account against the bank, live. */
  reconcile: async (userId: string) => {
    const { data } = await apiClient.get<ApiResponse<SavingsReconciliation>>(
      `/admin/savings/users/${userId}`,
    );
    return data.data;
  },

  /** Every account against the bank. One provider call per account. */
  checkAll: async () => {
    const { data } = await apiClient.post<ApiResponse<SavingsCheckResult>>(
      '/admin/savings/accounts/check',
    );
    return data.data;
  },

  stuck: async () => {
    const { data } = await apiClient.get<ApiResponse<StuckSavingsMove[]>>(
      '/admin/savings/moves/stuck',
    );
    return data.data;
  },

  /** What the bank statement shows. Maker-checker: may come back as an
   *  approval waiting on a second admin. */
  resolve: async (id: string, outcome: 'completed' | 'failed') => {
    const { data } = await apiClient.post<ApiResponse<MaybeApproval<unknown>>>(
      `/admin/savings/moves/${id}/resolve`,
      { outcome },
    );
    return data.data;
  },
};
